/**
 * @architecture-header 1
 * @layer module
 * @domain classrooms
 * @responsibility Declare the guardian-binding constants and derivations.
 * @state-owner dataset store holds guardian records
 * @allowed-imports shared view types
 * @forbidden React; network; component markup
 * @public-api classrooms module
 * @side-effects none
 * @tests workspace Playwright flows in frontend/apps/web/e2e/workspace.spec.ts
 */

import type {
  GuardianChannel,
  GuardianRecord,
  GuardianSource,
  GuardianState,
} from '../../../shared/lib/model';

export const GUARDIAN_MAX = 4;

export const CHANNEL_LABEL: Readonly<Record<GuardianChannel, string>> = {
  line: 'LINE',
  wa: 'WhatsApp',
  wechat: 'WeChat',
  sms: '簡訊',
};

export const CHANNEL_COLOR: Readonly<Record<GuardianChannel, string>> = {
  line: '#06C755',
  wa: '#25D366',
  wechat: '#07C160',
  sms: '#6b6b76',
};

export const CHANNEL_NOTE: Readonly<Record<GuardianChannel, string>> = {
  line: '加好友後用官方帳號推播，可以放卡片與按鈕',
  wa: '用 Business 範本訊息推播，家長回覆後才能自由對話',
  wechat: '服務號模板消息，需要中國主體',
  sms: '最通用但只能放短連結，成本最高',
};

export const CHANNEL_BINDING: Readonly<Record<GuardianChannel, readonly [string, string]>> = {
  line: [
    '加入 Vispo 官方帳號好友',
    '家長按下去會加你的 LINE 官方帳號，系統這時才拿得到他的 LINE userId。注意: 使用者看得到的那個 LINE ID 不能拿來發訊息。',
  ],
  wa: [
    '傳一則訊息給我們',
    '家長點下去會開啟 WhatsApp 並帶一則預填訊息，送出後我們才有他的號碼，也才開啟 24 小時對話窗口。',
  ],
  wechat: ['關注服務號', '家長掃碼關注你的 WeChat 服務號，之後用模板消息推播。'],
  sms: ['填手機號碼', '唯一需要家長自己輸入號碼的管道，填完會收到一次性驗證碼。'],
};

export const GUARDIAN_SOURCE: Readonly<Record<GuardianSource, readonly [string, string]>> = {
  roster: ['報名表', '從補習班報名資料匯入，號碼是家長自己留的'],
  teacher: ['老師輸入', '老師手動輸入的聯絡方式'],
  student: ['學生提供', '學生在自己的裝置上出示 QR 給家長掃'],
  link: ['公開連結', '掃班級 QR 或拿到轉傳連結自己來的，系統不知道他是誰'],
};

export const GUARDIAN_STATE_LABEL: Readonly<
  Record<GuardianState, readonly [string, 'done' | 'half' | 'warn', '' | 'wn']>
> = {
  linked: ['已綁定', 'done', ''],
  pending: ['邀請待確認', 'half', 'wn'],
  none: ['未綁定', 'warn', 'wn'],
};

export function guardianState(records: readonly GuardianRecord[]): GuardianState {
  if (records.some((record) => record.state === 'linked')) return 'linked';
  if (records.some((record) => record.state === 'pending')) return 'pending';
  return 'none';
}

export interface GuardianStats {
  readonly total: number;
  readonly linked: number;
  readonly pending: number;
  readonly none: number;
}

export function guardianStats(
  students: readonly string[],
  guardians: Readonly<Record<string, readonly GuardianRecord[]>>,
): GuardianStats {
  const stateOf = (student: string) => guardianState(guardians[student] ?? []);
  return {
    total: students.length,
    linked: students.filter((student) => stateOf(student) === 'linked').length,
    pending: students.filter((student) => stateOf(student) === 'pending').length,
    none: students.filter((student) => stateOf(student) === 'none').length,
  };
}
